import { css, cx, useTheme } from '@fable/theme'
import { Link } from 'react-router-dom'
import { Button, FlexBox, Text } from '@fable/components'
import { BlogEvent } from '../../../types'
import { getDatePattern, getDateString } from '../../../utils'
import { routes } from '../../community/routes'

export const HomeEventCard: React.VFC<{ event: BlogEvent }> = ({ event }) => {
  const { breakpoints, colors, mediaQueries } = useTheme()
  const eventLink = `${routes.events}/${event.slug}`
  const isLight = event.theme === 'light'

  const datePattern = getDatePattern(event.date, 'MMMM d, yyyy @ haaa z')
  const date = getDateString({
    date: event.date,
    pattern: datePattern,
  })

  const buttonStyles = css`
    margin-top: 16px;
    padding: 0 16px;
    width: auto;
    min-width: 113px;
    letter-spacing: -0.6px;
    pointer-events: auto;
    ${mediaQueries.mobileL} {
      margin-top: 36px;
    }
    ${mediaQueries.desktop} {
      min-width: 184px;
    }
  `

  return (
    <Link
      className={css`
        pointer-events: auto;
        ${mediaQueries.desktop} {
          pointer-events: none;
        }
      `}
      to={eventLink}
    >
      <div
        className={css`
          position: relative;
          background-color: ${colors.whiteFang};
          border-radius: 24px;
          border-top-right-radius: 100px;
          overflow: hidden;
          ${mediaQueries.desktop} {
            background-color: transparent;
            border-radius: 0;
            overflow: visible;
          }
        `}
      >
        <FlexBox
          className={css`
            flex-direction: column;
            ${mediaQueries.desktop} {
              flex-direction: row;
            }
          `}
        >
          <div
            className={css`
              position: relative;
              height: 260px;
              ${mediaQueries.mobile} {
                height: 60.664vw;
              }
              ${mediaQueries.mobileL} {
                height: min(65.664vw, 590px);
              }
              ${mediaQueries.desktop} {
                margin-right: 8.893vw;
                background-color: transparent;
                width: 41.791vw;
                height: min(38.98vw, 596px);
              }
            `}
          >
            <img
              data-testid="featuredEventImage"
              className={css`
                display: block;
                position: absolute;
                right: 0;
                top: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center;
                ${mediaQueries.mobileXS} {
                  object-position: left;
                }
                ${mediaQueries.desktop} {
                  position: relative;
                  border-radius: 28px;
                  border-top-left-radius: 90px;
                  object-position: center;
                  overflow: hidden;
                }
              `}
              src={`${event.image?.url}?q=100` || ''}
              alt={`${event.title} Event`}
            />
            {!event.book ? null : (
              <img
                data-testid="featuredEventBookCover"
                className={css`
                  width: 73px;
                  height: 110px;
                  background-color: lightblue;
                  border-radius: 4px;
                  position: absolute;
                  left: 23px;
                  bottom: -16px;
                  object-fit: cover;
                  ${mediaQueries.mobileXS} {
                    width: 108px;
                    height: 162px;
                    border-radius: 8px;
                  }
                  ${mediaQueries.mobileL} {
                    width: 15.234vw;
                    height: 23.662vw;
                    left: 61px;
                    bottom: -28px;
                  }
                  ${mediaQueries.desktop} {
                    width: 9.701vw;
                    height: 15.068vw;
                    left: auto;
                    right: -63px;
                  }
                `}
                src={event.book.bookCover}
                alt={`${event.book.title} Cover`}
              />
            )}
          </div>
          <div
            className={css`
              padding: 36px 23px 22px;
              ${mediaQueries.mobileL} {
                padding: 65px 61px 54px;
              }
              ${mediaQueries.desktop} {
                padding: 0;
                padding-top: 4.042vw;
              }
            `}
          >
            {!!date ? (
              <Text
                className={css`
                  color: ${colors.graphite};
                  ${mediaQueries.desktop} {
                    color: ${isLight ? colors.graphite : colors.almostWhite};
                  }
                `}
                text={date}
                type="body"
                sizing="L"
              />
            ) : null}
            <Text
              className={css`
                margin: 16px 0 12px;
                max-width: 294px;
                display: block;
                cursor: pointer;
                ${mediaQueries.mobileL} {
                  margin: 20px 0;
                  max-width: none;
                }
                ${mediaQueries.desktop} {
                  max-width: min(34.266vw, 669px);
                  color: ${isLight ? colors.graphite : colors.whiteFang};
                }
              `}
              text={event.title}
              type="header"
              custom={{
                fontSize: '38px',
                lineHeight: '40px',
                [mediaQueries.tablet]: {
                  fontSize: '56px',
                  lineHeight: '60px',
                },
                [mediaQueries.desktop]: {
                  fontSize: 'min(4.514vw, 76px)',
                  lineHeight: 'min(4.861vw, 76px)',
                },
              }}
            />
            <Text
              className={css`
                color: ${colors.graphite};
                ${mediaQueries.desktop} {
                  color: ${isLight ? colors.graphite : colors.almostWhite};
                  max-width: 436px;
                }
              `}
              type="body"
              sizing={{ base: 'M', mobileL: 'L' }}
              text={event.excerpt}
            />
            <Button
              className={cx(
                buttonStyles,
                css`
                  ${mediaQueries.desktop} {
                    display: none !important;
                  }
                `
              )}
            >
              RSVP
            </Button>
            <Button
              className={cx(
                buttonStyles,
                css`
                  @media only screen and (max-width: ${breakpoints.desktop}px) {
                    display: none !important;
                  }
                `
              )}
              height="48px"
              negative={!isLight}
              custom={
                !!event.rsvpButtonColor && {
                  backgroundColor: event.rsvpButtonColor,
                  borderColor: event.rsvpButtonColor,
                }
              }
            >
              Free with RSVP
            </Button>
          </div>
        </FlexBox>
      </div>
    </Link>
  )
}
